import { useMemo } from 'react';
import { useAccountStore } from '../stores/accountStore';
import { describeMarket } from '../lib/kalshiTicker';

function timeAgo(isoString: string): string {
  const seconds = Math.floor((Date.now() - new Date(isoString).getTime()) / 1000);
  if (seconds < 5) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  return `${Math.floor(seconds / 3600)}h ago`;
}

export function ActivityTicker() {
  const fills = useAccountStore((s) => s.activityTicker);
  const status = useAccountStore((s) => s.status);

  // Defensive sort by createdAt desc — processFill prepends, but bulk
  // refreshes from the relay aren't guaranteed to preserve order.
  const sortedFills = useMemo(
    () =>
      [...fills].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      ),
    [fills],
  );

  if (status !== 'connected') return null;
  if (sortedFills.length === 0) {
    return (
      <div className="text-muted-foreground px-3 py-2 text-xs">
        No recent fills
      </div>
    );
  }

  return (
    <div className="space-y-0.5">
      {sortedFills.map((fill) => {
        const { marketLabel, betTeam } = describeMarket(fill.ticker, fill.side);
        return (
          <div
            key={fill.fillId}
            className="hover:bg-muted/50 flex items-center justify-between gap-2 rounded px-2 py-1 text-xs"
          >
            <div className="flex items-center gap-1.5 overflow-hidden">
              <span
                className={
                  fill.action === 'buy'
                    ? 'shrink-0 font-medium text-green-400'
                    : 'shrink-0 font-medium text-red-400'
                }
              >
                {fill.action.toUpperCase()}
              </span>
              <span
                className={`shrink-0 rounded px-1 py-0.5 text-[9px] font-bold ${
                  fill.side === 'yes'
                    ? 'bg-green-500/20 text-green-400'
                    : 'bg-red-500/20 text-red-400'
                }`}
              >
                {betTeam ? `(${betTeam}) ` : ''}
                {fill.side.toUpperCase()}
              </span>
              <span className="truncate text-gray-300" title={fill.ticker}>
                {marketLabel}
              </span>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <span className="text-muted-foreground">{fill.count}</span>
              <span className="text-foreground">@ {fill.price}¢</span>
              <span className="text-muted-foreground/60">{timeAgo(fill.createdAt)}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
